import qs from 'qs';
import z from 'zod';
import { ContactType, FindAllContactsFilters } from '../app/lib/filters/dto';
import { contactsFiltersAdapter } from '../app/lib/utils';
import { ImportCSVResponseDTO } from '../app/pages/database/dto';
import { ProjectDTO } from '../app/pages/projects/dto';
import { ContactIdsDTO, ContactsCounterDTO, ContactsIdsResponseDTO, CounterDTO } from '../app/pages/targetList/contacts/dto';
import { FindAllTargetListsFilters, FindAllTargetListsResponse, TargetListDTO, TargetListStatusEnum } from '../app/pages/targetList/dto';
import { newTargetListSchema, updateTargetListSchema } from '../app/pages/targetList/editTargetListDetails/validation';
import { UserDTO } from '../app/pages/users/dto';
import { baseApi } from './_baseQuery';
import { keycloak } from '../app/lib/keycloak';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';


export const targetlistApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAllTargetList: build.query<{ targetLists: FindAllTargetListsResponse, teamLeaders: { user: UserDTO, avatar: string }[], commercials: { user: UserDTO, avatar: string }[] }, FindAllTargetListsFilters>({
            async queryFn(filters, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const targetListResponse = await fetchWithBQ(
                        {
                            url: '/target-lists',
                            params: qs.stringify(filters, {
                                arrayFormat: "repeat",
                                encode: true,
                            })
                        }
                    );
                    if (targetListResponse.error) return { error: targetListResponse.error };

                    const targetLists = targetListResponse.data as FindAllTargetListsResponse;
                    const targetListsData = targetLists.data;

                    const commercialIds = [...new Set(targetListsData?.flatMap(p => p.commercialIds || []).filter(id => id !== null))];
                    const teamLeaderIds = [...new Set(targetListsData?.flatMap(p => p.project.teamLeaderId || []).filter(id => id !== null))];

                    const commercialsResponse = await Promise.all(
                        commercialIds.map(async id => {
                            const userRes = await fetchWithBQ(`/users/${id}`);
                            if (userRes.error) return { error: userRes.error };

                            const user = userRes.data as UserDTO;

                            let avatar: string | null = null;
                            if (user.avatarObjectId) {
                                const avatarRes = await fetchWithBQ("/objects/file/" + user.avatarObjectId);
                                if (!avatarRes.error) {
                                    avatar = avatarRes.data as string;
                                }
                            }

                            return {
                                data: {
                                    user: user,
                                    avatar: avatar || ''
                                }
                            };
                        })
                    );

                    const teamLeaderResponse = await Promise.all(
                        teamLeaderIds.map(async id => {
                            const userRes = await fetchWithBQ(`/users/${id}`);
                            if (userRes.error) return { error: userRes.error };

                            const user = userRes.data as UserDTO;

                            let avatar: string | null = null;
                            if (user.avatarObjectId) {
                                const avatarRes = await fetchWithBQ("/objects/file/" + user.avatarObjectId);
                                if (!avatarRes.error) {
                                    avatar = avatarRes.data as string;
                                }
                            }

                            return {
                                data: {
                                    user: user,
                                    avatar: avatar || ''
                                }
                            };
                        })
                    );

                    const commercials = commercialsResponse
                        .filter(response => response && !response.error)
                        .map(response => response.data as { user: UserDTO, avatar: string });

                    const teamLeaders = teamLeaderResponse
                        .filter(response => response && !response.error)
                        .map(response => response.data as { user: UserDTO, avatar: string });


                    return {
                        data: {
                            targetLists,
                            teamLeaders: teamLeaders,
                            commercials: commercials
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch users or avatars',
                            data: error
                        }
                    };
                }
            },
            providesTags: ['Targetlist'],
        }),
        findAllTargetWithUsers: build.query<FindAllTargetListsResponse, FindAllTargetListsFilters>({
            query: (filters) => ({
                url: '/target-lists',
                params: qs.stringify(filters, {
                    arrayFormat: "repeat",
                    encode: true,
                })
            }),
            providesTags: ['Targetlist'],
        }),
        findSuspendedTargetList: build.query<FindAllTargetListsResponse, void>({
            query: () => ({
                url: '/target-lists',
                params: qs.stringify({
                    commercial: [],
                    itemsPerPage: 0,
                    project: [],
                    order: false,
                    page: 0,
                    sector: [],
                    category: [],
                    sort: "name",
                    state: [TargetListStatusEnum.SUSPENDED],
                    teamLeader: []
                }, {
                    arrayFormat: "repeat",
                    encode: true,
                })
            }),
            providesTags: [{ type: 'Targetlist', id: 'SUSPENDED' }],
        }),
        removeTargetList: build.mutation<{ success: boolean; id: string }, string>({
            query: (id) => ({
                url: `/target-lists/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, _ids) => ["Targetlist"],
        }),
        findOneTargetList: build.query<{ targetList: TargetListDTO | null, counters: ContactsCounterDTO[], contactsIds: ContactsIdsResponseDTO[], project: ProjectDTO }, string>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const targetListRes = await fetchWithBQ(`target-lists/${id}`);
                    if (targetListRes.error) return { error: targetListRes.error };
                    const targetList = targetListRes.data as TargetListDTO;

                    const projectResponse = await fetchWithBQ({
                        url: `projects/${targetList.projectId}`,
                    });
                    if (projectResponse.error) return { error: projectResponse.error };
                    const project = projectResponse.data as ProjectDTO;

                    const personCounterResponse = await fetchWithBQ({
                        url: `contacts/count`,
                        params: {
                            targetList: id,
                            contactType: ContactType.PERSON
                        }
                    });
                    if (personCounterResponse.error) return { error: personCounterResponse.error };

                    const companyCounterResponse = await fetchWithBQ({
                        url: `contacts/count`,
                        params: {
                            targetList: id,
                            contactType: ContactType.COMPANY
                        }
                    });
                    if (companyCounterResponse.error) return { error: companyCounterResponse.error };

                    let personIdsData: ContactIdsDTO[] = [];
                    let companyIdsData: ContactIdsDTO[] = [];

                    if (!keycloak.hasRealmRole('customer') && !keycloak.hasRealmRole('collaborator')) {
                        const personIdsResponse = await fetchWithBQ({
                            url: `contacts/ids`,
                            params: {
                                targetList: id,
                                contactType: ContactType.PERSON
                            }
                        });
                        if (personIdsResponse.error) return { error: personIdsResponse.error };
                        personIdsData = personIdsResponse.data as ContactIdsDTO[];

                        const companyIdsResponse = await fetchWithBQ({
                            url: `contacts/ids`,
                            params: {
                                targetList: id,
                                contactType: ContactType.COMPANY
                            }
                        });
                        if (companyIdsResponse.error) return { error: companyIdsResponse.error };
                        companyIdsData = companyIdsResponse.data as ContactIdsDTO[];
                    }

                    return {
                        data: {
                            project,
                            targetList,
                            counters: [{ dto: personCounterResponse.data as CounterDTO, contactType: ContactType.PERSON }, { dto: companyCounterResponse.data as CounterDTO, contactType: ContactType.COMPANY }],
                            contactsIds: [{ dto: personIdsData, contactType: ContactType.PERSON }, { dto: companyIdsData, contactType: ContactType.COMPANY }]
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch person or company',
                            data: error
                        }
                    };
                }
            },
            providesTags: (_result, _error, id) => [{ type: 'Targetlist', id }],
        }),
        updateTargetList: build.mutation<{ success: boolean; company: TargetListDTO }, { id: string, data: z.infer<typeof updateTargetListSchema> }>({
            query: ({ id, data }) => ({
                url: `/target-lists/${id}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Targetlist', id }, "Targetlist"],
        }),
        createTargetList: build.mutation<{ success: boolean; targetListId: string }, { data: z.infer<typeof newTargetListSchema> }>({
            query: ({ data }) => ({
                url: `/target-lists`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error) => [{ type: 'Targetlist', id: _result?.targetListId }, "Targetlist"],
        }),
        changeTargetListStatus: build.mutation<{ success: boolean }, { request: { state: TargetListStatusEnum.CONCLUDED | TargetListStatusEnum.INPROGRESS | undefined; id: string; }[] }>({
            query: (body) => ({
                url: `/target-lists/state`,
                method: 'PUT',
                body: body.request,
            }),
            invalidatesTags: ['Targetlist', { type: 'Targetlist', id: 'SUSPENDED' }],
        }),
        copyTargetList: build.mutation<{ targetListId: string }, { targetList: z.infer<typeof newTargetListSchema>, people: string[], companies: string[] }>({
            async queryFn(data, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const response = await fetchWithBQ({
                        url: `/target-lists`,
                        method: 'POST',
                        body: data.targetList,
                    });
                    if (response.error) return { error: response.error };
                    const targetListId = (response.data as { targetListId: string }).targetListId;

                    if (data.people.length > 0) {
                        const addPeopleResponse = await fetchWithBQ({
                            url: `contacts/bulk`,
                            method: 'POST',
                            body: {
                                targetListId: targetListId,
                                contactType: ContactType.PERSON,
                                contactIds: data.people
                            }
                        });
                        if (addPeopleResponse.error) return { error: addPeopleResponse.error };
                    }

                    if (data.companies.length > 0) {
                        const addCompaniesResponse = await fetchWithBQ({
                            url: `contacts/bulk`,
                            method: 'POST',
                            body: {
                                targetListId: targetListId,
                                contactType: ContactType.COMPANY,
                                contactIds: data.companies
                            }
                        });
                        if (addCompaniesResponse.error) return { error: addCompaniesResponse.error };
                    }
                    return { data: { targetListId } };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch person or company',
                            data: error
                        }
                    };
                }
            },
            invalidatesTags: ['Targetlist'],
        }),
    }),
});

export const {
    useFindAllTargetListQuery,
    useRemoveTargetListMutation,
    useFindOneTargetListQuery,
    useUpdateTargetListMutation,
    useCreateTargetListMutation,
    useFindSuspendedTargetListQuery,
    useChangeTargetListStatusMutation,
    useCopyTargetListMutation,
} = targetlistApi